import type { ReactNode } from "react";
import { format } from "date-fns";
import {
  CircleCheckIcon,
  CircleDotIcon,
  CirclePlayIcon,
  CircleStopIcon,
  GitMergeIcon,
  GitPullRequestIcon,
  PlayIcon,
  TerminalIcon,
} from "lucide-react";
import useSWR from "swr";
import { Section } from "@/components/section";
import { Chip } from "@/components/badge/chip";
import { AGENTS, CLIENTS, MODELS } from "@/components/prds/exec-menu";
import { PR_STATE } from "@/constants/pr-state";
import { DATE_TIME } from "@/lib/date";
import type { PrState } from "@/types/domain";

// PRD 执行时间线(WED-282):执行记录 + PR 开出 / 合并 + 成员开始 / 完成,
// 服务端合并好按时间倒序给出,这里只渲染。thread 不并入。没有事件时不显示。

type Base = { at: string; userId?: string; userName?: string };
type PrRef = { url: string; repo: string; number: number; title: string | null };
export type TimelineEvent = Base &
  (
    | {
        type: "exec";
        template: string;
        templateLabel: string;
        agent: string | null;
        client: string;
        model: string | null;
      }
    | ({ type: "pr_opened"; state: PrState; agent: string | null } & PrRef)
    | ({ type: "pr_merged" } & PrRef)
    | {
        type: "member_started" | "member_completed";
        engagementId: string;
        appName: string | null;
      }
    | {
        type: "session_started" | "session_ended";
        agent: string | null;
        client: string;
        sessionId: string;
      }
  );

export const timelinePath = (prdId: string) => `/prds/${prdId}/timeline`;

const labelOf = (list: { id: string; label: string }[], id: string | null) =>
  (id && list.find((o) => o.id === id)?.label) || id;

const execSummary = (e: Extract<TimelineEvent, { type: "exec" }>) => {
  const models = e.agent ? MODELS[e.agent as keyof typeof MODELS] : undefined;
  return [
    labelOf(AGENTS, e.agent) ?? "-",
    labelOf(CLIENTS, e.client),
    (e.model && (labelOf(models ?? [], e.model) ?? e.model)) || "默认",
  ].join(" / ");
};

const PrLink = ({ pr }: { pr: PrRef }) => (
  <a
    href={pr.url}
    target="_blank"
    rel="noreferrer"
    className="min-w-0 truncate hover:underline"
  >
    <span className="font-mono text-xs text-muted-foreground">
      {pr.repo}#{pr.number}
    </span>{" "}
    {pr.title}
  </a>
);

const memberBody = (e: { appName: string | null }, action: string) =>
  e.appName ? `${e.appName} · ${action}` : action;

const shortSession = (id: string) => id.slice(-6);

const describeEvent = (e: TimelineEvent): { icon: ReactNode; body: ReactNode } => {
  const icon = "h-4 w-4 shrink-0 text-muted-foreground";
  switch (e.type) {
    case "exec":
      return {
        icon: <PlayIcon className={icon} />,
        body: (
          <span>
            执行 {e.templateLabel}
            <span className="text-muted-foreground"> · {execSummary(e)}</span>
          </span>
        ),
      };
    case "pr_opened": {
      const state = PR_STATE[e.state] ?? PR_STATE.open;
      return {
        icon: <GitPullRequestIcon className={icon} />,
        body: (
          <>
            <span className="shrink-0">开出 PR</span>
            <PrLink pr={e} />
            <Chip tone={state.tone}>{state.label}</Chip>
          </>
        ),
      };
    }
    case "pr_merged":
      return {
        icon: <GitMergeIcon className={icon} />,
        body: (
          <>
            <span className="shrink-0">合并 PR</span>
            <PrLink pr={e} />
          </>
        ),
      };
    // 多 App 文档靠 App 名分辨是哪个 App 的进度;单 App 也带上,保持一致
    case "member_started":
      return { icon: <CirclePlayIcon className={icon} />, body: memberBody(e, "开始处理") };
    case "member_completed":
      return { icon: <CircleCheckIcon className={icon} />, body: memberBody(e, "完成") };
    case "session_started":
      return {
        icon: <TerminalIcon className={icon} />,
        body: `${labelOf(AGENTS, e.agent) ?? "Agent"} 会话开始 · ${shortSession(e.sessionId)}`,
      };
    case "session_ended":
      return {
        icon: <CircleStopIcon className={icon} />,
        body: `会话结束 · ${shortSession(e.sessionId)}`,
      };
    // 服务端新增事件类型而前端未跟上时兜底,不能让整页崩
    default:
      return {
        icon: <CircleDotIcon className={icon} />,
        body: String((e as { type?: unknown }).type ?? "unknown"),
      };
  }
};

export const PrdTimelineSection = ({ prdId }: { prdId: string }) => {
  const { data } = useSWR<TimelineEvent[]>([timelinePath(prdId)]);
  const events = Array.isArray(data) ? data : [];
  if (events.length === 0) return null;
  return (
    <Section title="Timeline">
      <ol aria-label="Timeline" className="space-y-1.5">
        {events.map((e, i) => {
          const { icon, body } = describeEvent(e);
          return (
            <li key={i} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm">
              {icon}
              <time
                dateTime={e.at}
                className="w-36 shrink-0 text-xs text-muted-foreground tabular-nums"
              >
                {format(new Date(e.at), DATE_TIME)}
              </time>
              {e.userName && <span className="shrink-0 font-medium">{e.userName}</span>}
              <span className="flex min-w-0 items-center gap-2">{body}</span>
            </li>
          );
        })}
      </ol>
    </Section>
  );
};
